<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import Tag       from 'primevue/tag'
import Select    from 'primevue/select'
import InputText from 'primevue/inputtext'
import Textarea  from 'primevue/textarea'
import AppDialog from '@/components/ui/AppDialog.vue'
import compIOCDetailDrawer from '@/components/hackwatch/iocs/compIOCDetailDrawer.vue'
import api from '@/api'
import { useI18n } from '@/i18n'
import { useNotify } from '@/services/useNotify'
import { useUserStore } from '@/stores/user'
import { useFormatDate } from '@/composables/useFormatDate'
import { useConfirmDelete } from '@/composables/useConfirmDelete'

const props = defineProps({
  groupId: { type: [String, Number], required: false },
})

const route  = useRoute()
const { t }  = useI18n()
const notify = useNotify()
const user   = useUserStore()
const { fmtDate }       = useFormatDate()
const { confirmDelete } = useConfirmDelete()

const groupId = computed(() => Number(props.groupId ?? route.params.id))

// ── State ─────────────────────────────────────────────────────────────────────
const items       = ref([])
const total       = ref(0)
const loading     = ref(false)
const error       = ref('')
const typeSummary = ref([])
const filterType  = ref(null)
let   lastQuery   = {}

// ── Drawer ────────────────────────────────────────────────────────────────────
const drawerVisible = ref(false)
const selectedIOC   = ref(null)

function openDetail(row) {
  selectedIOC.value   = row
  drawerVisible.value = true
}

// ── Columns ───────────────────────────────────────────────────────────────────
const columns = [
  { field: 'ioc_type',    header: t('hackwatch.iocs.colType'),       sortable: true  },
  { field: 'value',       header: t('hackwatch.iocs.colValue'),      sortable: true  },
  { field: 'threat_type', header: t('hackwatch.iocs.colThreatType'), sortable: true  },
  { field: 'confidence',  header: t('hackwatch.iocs.colConfidence'), sortable: true  },
  { field: 'note',        header: t('hackwatch.iocs.colNote'),       sortable: false },
  { field: 'created_at',  header: t('hackwatch.iocs.colCreated'),    sortable: true  },
]

// ── Type filter options (built from type_summary) ─────────────────────────────
const typeOptions = computed(() => [
  { label: t('hackwatch.iocs.filterTypePlaceholder'), value: null },
  ...typeSummary.value.map(s => ({
    label: `${s.ioc_type} (${s.count})`,
    value: s.ioc_type,
  })),
])

// ── Data loading ──────────────────────────────────────────────────────────────
async function load(q = {}) {
  if (!groupId.value) return
  lastQuery     = q
  loading.value = true
  error.value   = ''
  try {
    const params = {
      page:     q.page    ?? 1,
      limit:    q.limit   ?? 50,
      ioc_type: filterType.value || undefined,
      sortBy:   q.sortField || 'ioc_type',
      sortDir:  q.sortOrder === 1 ? 'asc' : 'desc',
    }
    const { data } = await api.getGroupIOCs(groupId.value, params)
    items.value       = data.rows        ?? []
    total.value       = data.total       ?? 0
    typeSummary.value = data.type_summary ?? []
  } catch (e) {
    error.value = e?.response?.data?.message || t('hackwatch.iocs.loadFailed')
  } finally {
    loading.value = false
  }
}

function onQueryChange(q) { load(q) }

onMounted(() => load())
watch(() => groupId.value, () => load())
watch(filterType, () => load({ ...lastQuery, page: 1 }))

// ── Dialog ────────────────────────────────────────────────────────────────────
const showDialog = ref(false)
const isAdding   = ref(false)
const saving     = ref(false)
const editingIOC = ref({ ioc_type: '', value: '', source: 'manual', note: '' })

const openCreate = () => {
  isAdding.value   = true
  editingIOC.value = { ioc_type: '', value: '', source: 'manual', note: '' }
  showDialog.value = true
}
const openEdit = (row) => {
  isAdding.value   = false
  editingIOC.value = { id: row.id, ioc_type: row.ioc_type, value: row.value, source: row.source ?? 'manual', note: row.note ?? '' }
  showDialog.value = true
}

const saveIOC = async () => {
  if (!editingIOC.value.ioc_type?.trim()) {
    notify.warn(t('common.warning'), t('hackwatch.iocs.typeMissing'))
    return
  }
  if (!editingIOC.value.value?.trim()) {
    notify.warn(t('common.warning'), t('hackwatch.iocs.valueMissing'))
    return
  }
  saving.value = true
  try {
    const { id, ...payload } = editingIOC.value
    if (isAdding.value) {
      await api.createGroupIOC(groupId.value, payload)
      notify.success(t('hackwatch.iocs.saved'), t('hackwatch.iocs.iocCreated'))
    } else {
      await api.updateIOC(id, payload)
      notify.success(t('hackwatch.iocs.saved'), t('hackwatch.iocs.iocUpdated'))
    }
    showDialog.value = false
    await load(lastQuery)
  } catch (e) {
    notify.error(t('hackwatch.iocs.saveFailed'), e.response?.data?.message || e.message)
  } finally {
    saving.value = false
  }
}

const deleteIOCs = async (rows) => {
  if (!await confirmDelete(t('hackwatch.iocs.iocDeleted'))) return
  try {
    await Promise.all(rows.map(r => api.deleteIOC(r.id)))
    notify.success(t('hackwatch.iocs.deleted'), t('hackwatch.iocs.iocDeleted'))
    await load(lastQuery)
  } catch (e) {
    notify.error(t('hackwatch.iocs.deleteFailed'), e.response?.data?.message || e.message)
  }
}

const tableActions = computed(() => [
  user.can('hackwatch_iocs.change') && {
    label:          t('hackwatch.iocs.editTooltip'),
    icon:           'pi pi-pencil',
    requiresSingle: true,
    command:        (rows) => openEdit(rows[0]),
  },
  user.can('hackwatch_iocs.delete') && {
    label:     t('hackwatch.iocs.deleteTooltip'),
    icon:      'pi pi-trash',
    severity:  'danger',
    separator: true,
    command:   deleteIOCs,
  },
].filter(Boolean))

// ── Type badge ────────────────────────────────────────────────────────────────
const TYPE_SEVERITY = {
  ip: 'danger', 'ip:port': 'danger',
  domain: 'warn', url: 'warn',
  md5: 'info', sha1: 'info', sha256: 'info',
  email: 'secondary', btc: 'secondary',
}
const typeSeverity = (type) => TYPE_SEVERITY[type?.toLowerCase()] ?? 'secondary'

// ── Threat type badge ─────────────────────────────────────────────────────────
const THREAT_SEVERITY = {
  botnet_cc: 'danger', payload_delivery: 'danger',
  cc_skimming: 'warn',
}
const threatSeverity = (tt) => THREAT_SEVERITY[tt?.toLowerCase()] ?? 'secondary'

// ── Confidence badge ──────────────────────────────────────────────────────────
const confidenceSeverity = (c) => {
  if (c == null) return 'secondary'
  if (c >= 75)   return 'success'
  if (c >= 40)   return 'warn'
  return 'danger'
}
</script>

<template>
  <div class="hw-card">
    <div class="hw-card-header">
      <i class="pi pi-database hw-card-icon" />
      <span class="hw-card-title">{{ t('hackwatch.iocs.legendTitle') }}</span>
    </div>
    <AppTableWidget
      :columns="columns"
      :data="items"
      :total="total"
      :loading="loading"
      lazy
      :searchable="false"
      :default-rows="10"
      :rows-options="[10, 25, 50]"
      :empty-text="t('hackwatch.iocs.noIOCs')"
      :actions="tableActions"
      row-clickable
      @refresh="load(lastQuery)"
      @query-change="onQueryChange"
      @row-click="openDetail"
    >
      <template #toolbar>
        <!-- IOC-Typ Filter -->
        <Select
          v-model="filterType"
          :options="typeOptions"
          option-label="label"
          option-value="value"
          :show-clear="true"
          size="small"
          class="w-12rem"
        />

        <pButton
          v-if="user.can('hackwatch_iocs.create')"
          icon="pi pi-plus"
          :label="t('hackwatch.iocs.addBtn')"
          size="small"
          @click="openCreate"
        />
      </template>

      <!-- Typ-Badge -->
      <template #cell-ioc_type="{ row }">
        <Tag :value="row.ioc_type" :severity="typeSeverity(row.ioc_type)" style="font-size: 0.75rem;" />
      </template>

      <!-- Wert -->
      <template #cell-value="{ row }">
        <span v-tooltip.top="row.value.length > 60 ? row.value : undefined"
              style="font-family: monospace; font-size: 0.82rem; word-break: break-all;">
          {{ row.value.length > 60 ? row.value.slice(0, 57) + '…' : row.value }}
        </span>
      </template>

      <!-- Bedrohungstyp -->
      <template #cell-threat_type="{ row }">
        <Tag
          v-if="row.threat_type"
          :value="row.threat_type"
          :severity="threatSeverity(row.threat_type)"
          style="font-size: 0.72rem;"
        />
        <span v-else class="text-secondary">—</span>
      </template>

      <!-- Konfidenz -->
      <template #cell-confidence="{ row }">
        <Tag
          v-if="row.confidence != null"
          :value="row.confidence + '%'"
          :severity="confidenceSeverity(row.confidence)"
          style="font-size: 0.72rem;"
        />
        <span v-else class="text-secondary">—</span>
      </template>

      <!-- Datum -->
      <template #cell-created_at="{ row }">{{ fmtDate(row.created_at) }}</template>

    </AppTableWidget>

    <div v-if="error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>

    <compIOCDetailDrawer v-model="drawerVisible" :ioc="selectedIOC" />

    <!-- Dialog -->
    <AppDialog
      v-model="showDialog"
      :title="isAdding ? t('hackwatch.iocs.dialogCreate') : t('hackwatch.iocs.dialogEdit')"
      size="md"
      :show-footer="true"
      :maximizable="false"
      :loading="saving"
      @submit="saveIOC"
      @cancel="showDialog = false"
    >
      <div class="dialog-form">
        <div class="form-row-2">
          <div class="form-field required">
            <label>{{ t('hackwatch.iocs.formType') }}</label>
            <InputText v-model.trim="editingIOC.ioc_type" class="w-full"
              :placeholder="t('hackwatch.iocs.formTypePlaceholder')" />
          </div>
          <div class="form-field">
            <label>{{ t('hackwatch.iocs.formSource') }}</label>
            <InputText v-model.trim="editingIOC.source" class="w-full" placeholder="manual" />
          </div>
        </div>
        <div class="form-field required">
          <label>{{ t('hackwatch.iocs.formValue') }}</label>
          <InputText v-model.trim="editingIOC.value" class="w-full"
            :placeholder="t('hackwatch.iocs.formValuePlaceholder')" />
        </div>
        <div class="form-field">
          <label>{{ t('hackwatch.iocs.formNote') }}</label>
          <Textarea v-model.trim="editingIOC.note" rows="2" class="w-full" />
        </div>
      </div>
    </AppDialog>
  </div>
</template>
